import React from 'react';
import {Link} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Button} from 'src/design/atoms/Button';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Text} from 'src/design/atoms/Text';
import type {
  CourseSessionTrackerResponse} from 'src/api/ProactApiClient';
import {
  CourseAssignmentStateEnum
} from 'src/api/ProactApiClient';

export interface SessionGatewayAlertProps {
  tracker: CourseSessionTrackerResponse;
}
export const SessionGatewayAlert: React.FC<SessionGatewayAlertProps> = ({
  tracker,
}) => {
  const {assignment, unmetDependencyCourseNames} = tracker;
  const assignmentId = assignment.id;

  const isBlockedSession = unmetDependencyCourseNames.length > 0;

  let title = 'UNKNOWN';
  let body = 'UNKNOWN';
  const names = unmetDependencyCourseNames.join(', ');

  switch (assignment.state) {
    case CourseAssignmentStateEnum.Open:
      if (isBlockedSession === true) {
        title = 'You have some other training to complete first.';
        body = `You will need to complete these courses before you can start this one: ${names}.`;
      } else {
        title = 'Ready to get started?';
        body =
          'It looks like you need to complete this course. Click the "Get Started" button to begin.';
      }
      break;
    case CourseAssignmentStateEnum.Pending:
      title = 'Pick up where you left off.';
      body =
        'It looks like you did still have some work to do. Click the "Continue" button to continue your training.';
      break;
    case CourseAssignmentStateEnum.Locked:
      title = 'Training locked';
      body = `It looks like you attempted this course too many times. You can try again on ${assignment.expectedUnlockAt
        ?.toString()
        .toShortDate()}.`;
      break;
    case CourseAssignmentStateEnum.Complete:
      title = 'No action required!';
      body = 'You have successfully completed this course.';

      if (assignment.expectedExpirationAt !== null) {
        body += ` This course will not need to be renewed until ${assignment.expectedExpirationAt
          ?.toString()
          .toShortDate()}.`;
      }
      break;
    case CourseAssignmentStateEnum.Invalid:
    default:
      break;
  }

  const isClassroomCourse =
    assignment.isClassroomOnly === true &&
    (assignment.state == CourseAssignmentStateEnum.Open ||
      assignment.state == CourseAssignmentStateEnum.Pending);
  if (isClassroomCourse) {
    title = 'This course is completed in a classroom.';
    body =
      'There is no online content for this course. Your trainer will mark it complete after you attend the class.';
  }

  const showButton =
    !isClassroomCourse &&
    !isBlockedSession &&
    (assignment.state == CourseAssignmentStateEnum.Open ||
      assignment.state == CourseAssignmentStateEnum.Pending);
  const buttonText =
    assignment.state == CourseAssignmentStateEnum.Open
      ? 'Get Started'
      : 'Continue';
  const color = isClassroomCourse
    ? 'blue'
    : assignment.state == CourseAssignmentStateEnum.Complete
      ? 'green'
      : assignment.state == CourseAssignmentStateEnum.Locked
      ? 'red'
      : 'yellow';

  return (
    <Alert title={title} color={color}>
      <Text>{body}</Text>
      {showButton && (
        <Box mt="md">
          <Button
            size="sm"
            component={Link}
            to={PathUtility.TrainingSessionTracker(String(assignmentId))}>
            {buttonText}
          </Button>
        </Box>
      )}
    </Alert>
  );
};
